import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UploadSimple, FileArrowUp, PencilSimple, TrashSimple, ArrowsInLineVertical, Plus, Check, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { projectApi, chapterApi } from "@/api";
import { useProject } from "@/pages/ProjectShell";

export default function Structure() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { loadProject } = useProject();
  const [chapters, setChapters] = useState([]);
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const load = () => chapterApi.list(pid).then(setChapters);
  useEffect(() => { load(); }, [pid]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await projectApi.importFile(pid, fd);
      toast.success(`${res.chapter_count} chapitres détectés`);
      await load();
      loadProject();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadDemo = async () => {
    setUploading(true);
    try {
      const res = await projectApi.demo(pid, 12);
      toast.success(`${res.chapter_count} chapitres détectés (démo)`);
      await load();
      loadProject();
    } finally { setUploading(false); }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const rename = async (id) => {
    await chapterApi.update(pid, id, { title: editTitle });
    setEditing(null);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce chapitre ?")) return;
    await chapterApi.remove(pid, id);
    toast.success("Chapitre supprimé");
    load(); loadProject();
  };
  const merge = async () => {
    if (selected.length < 2) return toast.error("Sélectionnez au moins 2 chapitres");
    await chapterApi.merge(pid, selected);
    toast.success("Chapitres fusionnés");
    setSelected([]);
    load(); loadProject();
  };
  const addChapter = async () => {
    await chapterApi.add(pid, { title: `Nouveau chapitre`, text: "" });
    load(); loadProject();
  };
  const split = async (id) => {
    const c = await chapterApi.get(pid, id);
    const pos = Math.floor((c.chapter.working_text || "").length / 2);
    await chapterApi.split(pid, id, pos);
    toast.success("Chapitre séparé en deux");
    load(); loadProject();
  };

  return (
    <div className="p-8 max-w-4xl fade-up">
      <p className="overline text-primary mb-1">Structure du manuscrit</p>
      <h1 className="font-serif text-4xl font-light tracking-tight mb-6">Chapitres</h1>

      {chapters.length === 0 ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files[0]); }}
          className={`border-2 border-dashed rounded-lg py-20 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-border"}`}
          data-testid="import-dropzone"
        >
          <FileArrowUp size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
          <p className="font-serif text-2xl mb-2">Importez votre manuscrit</p>
          <p className="text-muted-foreground mb-6">Glissez un fichier ici, ou choisissez-en un. Formats : DOCX, DOC, TXT, RTF.</p>
          <div className="flex items-center justify-center gap-3">
            <input ref={fileRef} type="file" accept=".docx,.doc,.txt,.rtf" className="hidden" onChange={(e) => doUpload(e.target.files[0])} data-testid="import-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm gap-2" data-testid="import-choose-file">
              <UploadSimple size={18} /> {uploading ? "Import…" : "Choisir un fichier"}
            </Button>
            <Button variant="outline" onClick={loadDemo} disabled={uploading} className="rounded-sm" data-testid="import-demo">
              Charger la démo
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-serif text-2xl text-foreground">{chapters.length}</span> chapitres détectés
            </p>
            <div className="flex gap-2">
              {selected.length >= 2 && (
                <Button size="sm" variant="outline" onClick={merge} className="rounded-sm gap-1" data-testid="merge-chapters">
                  <ArrowsInLineVertical size={16} /> Fusionner ({selected.length})
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={addChapter} className="rounded-sm gap-1" data-testid="add-chapter"><Plus size={16} /> Ajouter</Button>
              <input ref={fileRef} type="file" accept=".docx,.doc,.txt,.rtf" className="hidden" onChange={(e) => doUpload(e.target.files[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm gap-1"><UploadSimple size={16} /> Réimporter</Button>
            </div>
          </div>

          <div className="space-y-2">
            {chapters.map((c) => (
              <div key={c.id} data-testid={`structure-chapter-${c.index}`} className="flex items-center gap-3 border border-border bg-surface rounded-sm px-4 py-3 hover:bg-surface-hover transition-colors group">
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="accent-primary" data-testid={`select-chapter-${c.index}`} />
                <span className="font-mono text-xs text-muted-foreground w-8">{c.index + 1}</span>
                {editing === c.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-8" autoFocus onKeyDown={(e) => e.key === "Enter" && rename(c.id)} />
                    <button onClick={() => rename(c.id)} className="text-primary" data-testid={`confirm-rename-${c.index}`}><Check size={18} /></button>
                    <button onClick={() => setEditing(null)} className="text-muted-foreground"><X size={18} /></button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => navigate(`/project/${pid}/editor/${c.id}`)} className="flex-1 text-left font-serif text-lg hover:text-primary transition-colors truncate">{c.title}</button>
                    <span className="text-xs text-muted-foreground font-mono">{c.word_count} mots</span>
                    <span className={`h-2 w-2 rounded-full ${c.analysis_status === "done" ? "bg-emerald-500" : c.analysis_status === "processing" ? "bg-amber-500 animate-pulse" : "bg-border"}`} title={c.analysis_status} />
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditing(c.id); setEditTitle(c.title); }} className="h-7 w-7 grid place-items-center rounded-sm hover:bg-background" data-testid={`rename-chapter-${c.index}`}><PencilSimple size={15} /></button>
                      <button onClick={() => split(c.id)} title="Séparer" className="h-7 w-7 grid place-items-center rounded-sm hover:bg-background text-xs">½</button>
                      <button onClick={() => del(c.id)} className="h-7 w-7 grid place-items-center rounded-sm hover:bg-background text-destructive" data-testid={`delete-chapter-${c.index}`}><TrashSimple size={15} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
